import { useMemo, useState } from 'react';
import { ImportExportToolbar } from './import-export-toolbar';
import { ImportExportModal } from './import-export-modal';
import { ImportExportAdapter } from './types';

/**
 * USAGE EXAMPLE:
 * const { ImportExportToolbar, Import, Export } = useImportExport(adapter, initialImportParams, initialExportParams, callbacks);
 *
 * <ImportExportToolbar>
 *   <AnyAdditionalContentToShowToTheRightOfTheImportAndExportButtons>
 * </ImportExportToolbar>
 *
 * see products-adapter.tsx for an example of how to use the adapter.
 *
 */

export const useImportExport = <TI, TE>(
  adapter: ImportExportAdapter<TI, TE>,
  initialImportParams: TI,
  initialExportParams: TE,
  callbacks?: {
    onImportSuccess?: (result: unknown) => void;
    onExportSuccess?: (result: BlobPart) => void;
    onExportError?: (error: unknown) => void;
    onImportError?: (error: unknown) => void;
  },
) => {
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const sharedProps = useMemo(() => {
    return {
      adapter,
      initialImportParams,
      initialExportParams,
      callbacks,
    };
  }, [adapter, initialImportParams, initialExportParams, callbacks]);

  const Import = useMemo(
    () => () =>
      <ImportExportModal action="import" {...sharedProps} isOpen={isImportOpen} setIsOpen={setIsImportOpen} />,
    [sharedProps, isImportOpen, setIsImportOpen],
  );

  const Export = useMemo(
    () => () =>
      <ImportExportModal action="export" {...sharedProps} isOpen={isExportOpen} setIsOpen={setIsExportOpen} />,
    [sharedProps, isExportOpen, setIsExportOpen],
  );

  return {
    ImportExportToolbar: ({ children }: { children: React.ReactNode }) => (
      <ImportExportToolbar
        children={children}
        setIsExportOpen={setIsExportOpen}
        setIsImportOpen={setIsImportOpen}
        Import={Import}
        Export={Export}
      />
    ),
    Import,
    Export,
  };
};
